import { useMemo } from "react"
import { useSessionStorage } from "usehooks-ts"
import { productColumnNaming } from "~/components/planning/ProductTable/utils/columnUtils"
import { ProductTableColumn } from "~/constants/productTableColumns"
import { useNoosSupplyPlanDetailContext } from "~/context/NoosSupplyPlanDetailProvider"
import { useVariantsByIds } from "~/hooks/useVariants"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { useAvailabilityFromProjection } from "~/hooks/useAvailabilityFromProjection"
import { availabilityCellValue } from "~/utils/availabilityCell"
import { DateFormatType, formatDate } from "~/utils/datetime"
import type { LinkFilter } from "~/utils/deepLinkFilter"
import { sortWorstFirst } from "./availabilitySort"
import type { MonthColumn } from "./useNoosAvailabilityPeriod"
import { useNoosPlanVariantIds } from "./useNoosPlanVariantIds"

export const UNCATEGORIZED_LABEL = "Uncategorized"

export const AVAILABILITY_PATH = "/procurement/supply-plan/noos/$supplyPlanId/availability" as const

export const AVAILABILITY_GROUP_BY_VALUES = [
  "variant",
  "product",
  "category",
  "brand",
  "color",
  "gender",
  "vendor",
] as const
export type AvailabilityGroupBy = (typeof AVAILABILITY_GROUP_BY_VALUES)[number]

export const isAvailabilityGroupBy = (value: unknown): value is AvailabilityGroupBy =>
  AVAILABILITY_GROUP_BY_VALUES.includes(value as AvailabilityGroupBy)

type Grouping = {
  column: ProductTableColumn
  /** The Availability tab's filter field that narrows it to one group. */
  filterField: string
  /** Group keys and filter values; none puts the variant in the "no value" group. */
  values: (variant: IVariantSummary) => string[]
  label?: (variant: IVariantSummary) => string
}

const trimmed = (value: string | null | undefined): string[] => {
  const t = value?.trim()
  return t ? [t] : []
}

const GROUPINGS: Record<AvailabilityGroupBy, Grouping> = {
  variant: {
    column: ProductTableColumn.Variant,
    filterField: "variantId",
    values: (v) => [v.variantId],
    label: (v) => v.variantDisplayName,
  },
  product: {
    column: ProductTableColumn.Product,
    filterField: "productId",
    values: (v) => [v.productId],
    label: (v) => v.productName,
  },
  category: {
    column: ProductTableColumn.Category,
    filterField: "category",
    values: (v) => trimmed(v.category),
  },
  brand: {
    column: ProductTableColumn.Brand,
    filterField: "brand",
    values: (v) => trimmed(v.brand),
  },
  color: {
    column: ProductTableColumn.Color,
    filterField: "color",
    values: (v) => trimmed(v.color),
  },
  gender: {
    column: ProductTableColumn.Gender,
    filterField: "gender",
    values: (v) => trimmed(v.gender),
  },
  // Under every vendor, as the report's Vendor filter matches any of a variant's vendors.
  vendor: {
    column: ProductTableColumn.Vendor,
    filterField: "vendors",
    values: (v) => [...new Set(v.vendors.flatMap((vendor) => trimmed(vendor.name)))],
  },
}

/** "Category", "Vendor", …: the column title, so the options match the Availability tab. */
export const groupByTitle = (groupBy: AvailabilityGroupBy): string =>
  productColumnNaming[GROUPINGS[groupBy].column].columnTitle

export const GROUP_BY_OPTIONS = AVAILABILITY_GROUP_BY_VALUES.map((value) => ({
  value,
  label: groupByTitle(value),
}))

/** Rows of a single variant have nothing to expand into. */
export const isExpandableGroupBy = (groupBy: AvailabilityGroupBy) => groupBy !== "variant"

function emptyGroupLabel(groupBy: AvailabilityGroupBy): string {
  return groupBy === "category" ? UNCATEGORIZED_LABEL : `No ${groupByTitle(groupBy).toLowerCase()}`
}

/** The overview card's "Group rows by", remembered per plan like its period and warehouse. */
export function useNoosAvailabilityGroupBy() {
  const { supplyPlan } = useNoosSupplyPlanDetailContext()
  const [stored, setGroupBy] = useSessionStorage<AvailabilityGroupBy>(
    `noos-availability-group-by-${supplyPlan.id}`,
    "category",
  )
  return [isAvailabilityGroupBy(stored) ? stored : "category", setGroupBy] as const
}

/** Overview link → report: `field` alone links to every value of it. */
export type AvailabilityReportSearch = {
  field?: AvailabilityGroupBy
  value?: string
  month?: string
}

/**
 * Every link from the overview card states its scope on the grouped field: a group sets the
 * report's filter on that field, anything else (Totals, View full report, the "no value" group,
 * which has nothing to filter on) clears it, so a filter on that field left in the report's
 * persisted scope can't narrow it. The planner's filters on other fields stay.
 */
export function availabilityReportSearch(
  field: AvailabilityGroupBy,
  value: string | undefined,
  dateColumn?: MonthColumn,
): AvailabilityReportSearch {
  const month = dateColumn
    ? formatDate(dateColumn.firstDay, DateFormatType.YearMonthKey)
    : undefined
  return { field, value, month }
}

/** The report's filter for an overview link, applied for that visit. */
export function availabilityLinkFilter(
  field: AvailabilityGroupBy | undefined,
  value: string | undefined,
): LinkFilter | undefined {
  if (!field) return undefined
  const filterField = GROUPINGS[field].filterField
  if (value === undefined) return { field: filterField, clear: true }
  return { field: filterField, operator: "oneOf", value: "", by: "variantId", values: [value] }
}

export type Cell = ReturnType<typeof availabilityCellValue>
export type CategoryProjection = Parameters<typeof availabilityCellValue>[2]
export type GroupEntry = {
  key: string
  label: string
  /** The report filter's value; undefined for the "no value" group. */
  value: string | undefined
  variants: IVariantSummary[]
  cells: Cell[]
}

/**
 * The plan's variants grouped by `groupBy`, with each group's availability cells, plus the whole
 * plan's cells for the Totals row. Groups come worst first after lead time, as in the drilldown.
 */
export function useGroupedAvailability(
  dateColumns: MonthColumn[],
  warehouseId: string | undefined,
  groupBy: AvailabilityGroupBy,
) {
  const { variantIds } = useNoosPlanVariantIds()
  const allVariants = useVariantsByIds(variantIds)

  const { byColumn, horizonByColumn, isLoading } = useAvailabilityFromProjection(
    variantIds,
    warehouseId,
    dateColumns,
    { keepPreviousWindow: true },
  )
  const projection = useMemo(() => ({ byColumn, horizonByColumn }), [byColumn, horizonByColumn])
  const preorders = useMemo(() => ({ enabled: false, warehouseId }), [warehouseId])

  const groups = useMemo((): GroupEntry[] => {
    const grouping = GROUPINGS[groupBy]
    const map = new Map<string, Omit<GroupEntry, "cells">>()
    const add = (value: string | undefined, variant: IVariantSummary) => {
      // A key no real value can take, so the "no value" group can't merge with one.
      const key = value ?? "\u0000"
      const group = map.get(key)
      if (group) group.variants.push(variant)
      else {
        const label =
          value === undefined ? emptyGroupLabel(groupBy) : (grouping.label?.(variant) ?? value)
        map.set(key, { key, label, value, variants: [variant] })
      }
    }
    for (const variant of allVariants) {
      const values = grouping.values(variant)
      if (values.length === 0) add(undefined, variant)
      for (const value of values) add(value, variant)
    }
    const groups = [...map.values()].map((group) => ({
      ...group,
      cells: dateColumns.map((c) =>
        availabilityCellValue(group.variants, c, projection, preorders),
      ),
    }))
    groups.sort((a, b) => b.variants.length - a.variants.length || a.label.localeCompare(b.label))
    // A group's months count from its shortest lead time: a buy placed today reaches them for
    // some of its variants.
    return sortWorstFirst(
      groups,
      (g) => g.cells,
      (g) => g.variants.reduce((min, v) => Math.min(min, v.settings.totalLeadTimeDays), Infinity),
      dateColumns,
    )
  }, [allVariants, groupBy, dateColumns, projection, preorders])

  const totals = useMemo(
    () => dateColumns.map((c) => availabilityCellValue(allVariants, c, projection, preorders)),
    [allVariants, dateColumns, projection, preorders],
  )

  return { groups, totals, variantIds, projection, preorders, isLoading }
}
